import { Injectable } from '@angular/core';
import { TeamMember, User, UserService } from '@shared';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { SharedResourcesLoadedObj } from 'mojo-share-resource';

import { convertUserToTeamMember } from '@modules/jobs-page/utils/utils';
import { SharedResourcesType } from '@core/shared-resources/shared-resources.model';
import { getRouterState } from '@app/app.reducer';
import {
  getCurrentSharedResource,
  getCurrentSharedResourceTeamMembers,
} from '../store/shared-resources.selectors';

@Injectable()
export class SharedResourcesAutocompleteService {
  constructor(private userService: UserService, private store: Store<{}>) {}
  search(userName: string): Observable<TeamMember[]> {
    return combineLatest([
      this.userService.getFilteredUsers(userName),
      this.getCurrentTeamMembers(),
    ]).pipe(
      switchMap(([filteredUsers, teamMembers]) =>
        this.forPrivateTalentPoolAllowOnlyCorrectTeamMembers(
          this.filterOutAlreadyUsedUsers(filteredUsers, teamMembers),
        ),
      ),
    );
  }

  private filterOutAlreadyUsedUsers(
    filteredUsers: User[],
    teamMembersList: TeamMember[],
  ): TeamMember[] {
    return filteredUsers
      .map((user) => convertUserToTeamMember(user))
      .filter(
        (user) =>
          !teamMembersList.some((teamMember) => teamMember.id.toString() === user.id.toString()),
      );
  }

  private getCurrentTeamMembers(): Observable<TeamMember[]> {
    return this.store.pipe(select(getCurrentSharedResourceTeamMembers));
  }

  private forPrivateTalentPoolAllowOnlyCorrectTeamMembers(
    searchResult: TeamMember[],
  ): Observable<TeamMember[]> {
    return this.store.select(getRouterState).pipe(
      withLatestFrom(this.store.select(getCurrentSharedResource)),
      switchMap(([routerState, currentResource]) => {
        if (
          currentResource.resourceType === SharedResourcesType.note &&
          routerState.url.includes('talent-pools')
        ) {
          //if the talent pool is private, in notes we should not have team members outside talent-pool's team members
          return this.store
            .select(
              SharedResourcesLoadedObj.getLoadedSharedResourcesByTypeAndId(
                SharedResourcesType.talentPool,
                routerState.params.id.toString(),
              ),
            )
            .pipe(
              map((talentPoolParentResource) => {
                if (talentPoolParentResource?.teamMembers?.length > 0) {
                  return searchResult.filter((allowedTeamMember) =>
                    talentPoolParentResource.teamMembers
                      .map((t) => t.id)
                      .includes(allowedTeamMember.id),
                  );
                }
                return searchResult;
              }),
            );
        }
        return of(searchResult);
      }),
    );
  }
}
